import type { FormEvent } from 'react';
import React from 'react';

import { Box, FormField, FormHelp, Input, Label, Paragraph, Textarea, TextButton } from '@zillow/constellation-10';

import type Field from '../../../../models/Field';
import { PERSON } from '../../../../constants/index';
import { FULLSTORY_EXCLUDE } from '../../../../constants/analytics';
import PhoneInput from '../../../Widgets/PhoneInput';

type Props = {
  index: number;
  onFieldChange: (event: FormEvent<HTMLElement>) => void;
  onRemovePerson: (event: FormEvent<HTMLElement>) => void;
  firstNameField: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  firstNameValue: any;
  lastNameField: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lastNameValue: any;
  phoneNumberField: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  phoneNumberValue: any;
  emailField: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emailValue: any;
  mailingAddressField?: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mailingAddressValue?: any;
  personTypeLabel: string;
  addressLabel: string;
  className: string;
};

PersonInformation.defaultProps = {
  addressLabel: PERSON.LABEL.MAILING_ADDRESS,
};

export default function PersonInformation({
  index,
  firstNameField,
  firstNameValue,
  lastNameField,
  lastNameValue,
  phoneNumberField,
  phoneNumberValue,
  emailField,
  emailValue,
  mailingAddressField,
  mailingAddressValue,
  onFieldChange,
  onRemovePerson,
  personTypeLabel,
  addressLabel,
}: Props): JSX.Element {
  return (
    <Box
      key={index}
      className={FULLSTORY_EXCLUDE}
      css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'layout.tighter' }}
    >
      {index > 0 && (
        <Box css={{ gridColumnEnd: 'span 12' }}>
          <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Paragraph textStyle="body-bold">
              {personTypeLabel} #{index + 1}
            </Paragraph>
            <TextButton data-test="remove-person" data-index={index} onClick={onRemovePerson}>
              Delete
            </TextButton>
          </Box>
        </Box>
      )}
      <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
        <FormField
          required
          error={firstNameField.showOnError(PERSON.ERROR_MESSAGE.FIRST_NAME) ? true : false}
          label={<Label id={`${personTypeLabel}#${index + 1}-first-name`}>{PERSON.LABEL.FIRST_NAME}</Label>}
          control={
            <Input
              aria-labelledby={`${personTypeLabel}#${index + 1}-first-name`}
              data-field-name={firstNameField.fieldName}
              type="fname"
              value={firstNameValue}
              onChange={onFieldChange}
            />
          }
          description={
            firstNameField.showOnError(PERSON.ERROR_MESSAGE.FIRST_NAME) && (
              <FormHelp>{firstNameField.showOnError(PERSON.ERROR_MESSAGE.FIRST_NAME)}</FormHelp>
            )
          }
        />
      </Box>
      <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
        <FormField
          required
          error={lastNameField.showOnError(PERSON.ERROR_MESSAGE.LAST_NAME) ? true : false}
          label={<Label id={`${personTypeLabel}#${index + 1}-last-name`}>{PERSON.LABEL.LAST_NAME}</Label>}
          control={
            <Input
              aria-labelledby={`${personTypeLabel}#${index + 1}-last-name`}
              data-field-name={lastNameField.fieldName}
              type="lname"
              value={lastNameValue}
              onChange={onFieldChange}
            />
          }
          description={
            lastNameField.showOnError(PERSON.ERROR_MESSAGE.LAST_NAME) && (
              <FormHelp>{lastNameField.showOnError(PERSON.ERROR_MESSAGE.LAST_NAME)}</FormHelp>
            )
          }
        />
      </Box>
      {mailingAddressField && (
        <Box css={{ gridColumnEnd: 'span 12' }}>
          <FormField
            required
            css={{ marginBottom: 'layout.tighter' }}
            error={mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) ? true : false}
            label={<Label id={`${personTypeLabel}#${index + 1}-mailing-address`}>{addressLabel}</Label>}
            control={
              <Textarea
                aria-labelledby={`${personTypeLabel}#${index + 1}-mailing-address`}
                data-field-name={mailingAddressField.fieldName}
                value={mailingAddressValue}
                onChange={onFieldChange}
                css={{ resize: 'vertical' }}
                rows={3}
              />
            }
            description={
              mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) ? (
                <FormHelp>{mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)}</FormHelp>
              ) : (
                <FormHelp>Street, city, state, ZIP code</FormHelp>
              )
            }
          />
        </Box>
      )}
      <Box css={{ gridColumnEnd: 'span 12' }}>
        <FormField
          required
          css={{ marginBottom: 'layout.tighter' }}
          error={
            emailField.showOnError(
              emailValue.includes(' ') ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
            )
              ? true
              : false
          }
          label={<Label id={`${personTypeLabel}#${index + 1}-email`}>{PERSON.LABEL.EMAIL}</Label>}
          control={
            <Input
              aria-labelledby={`${personTypeLabel}#${index + 1}-email`}
              data-field-name={emailField.fieldName}
              value={emailValue}
              onChange={onFieldChange}
              // type='email' Disabled due to inconsistent/unpredicatable support across browsers
            />
          }
          description={
            emailField.showOnError(
              emailValue.includes(' ') ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
            ) && (
              <FormHelp>
                {emailField.showOnError(
                  emailValue.includes(' ')
                    ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE
                    : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
                )}
              </FormHelp>
            )
          }
        />
      </Box>
      {phoneNumberField && (
        <Box css={{ gridColumnEnd: 'span 12' }}>
          <FormField
            required
            error={phoneNumberField.hasError}
            label={<Label id={`${personTypeLabel}#${index + 1}-phone-number`}>{PERSON.LABEL.PHONE_NUMBER}</Label>}
            control={
              <PhoneInput
                aria-labelledby={`${personTypeLabel}#${index + 1}-phone-number`}
                data-field-name={phoneNumberField.fieldName}
                value={phoneNumberValue}
                onChange={onFieldChange}
                error={phoneNumberField.hasError}
              />
            }
            description={phoneNumberField.hasError && <FormHelp>{phoneNumberField.getErrorMessage()}</FormHelp>}
          />
        </Box>
      )}
    </Box>
  );
}
